import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Button, Select, Input, Modal, message } from 'antd';
import { useLocation, useSearchParams } from 'react-router-dom';
import { AuditOutlined } from '@ant-design/icons';
import DOLayout from '../../components/directOrders/DOLayout';
import OrdersExplorer from '../../components/directOrders/OrdersExplorer';
import ApprovalTrack from '../../components/directOrders/ApprovalTrack';
import IconTile from '../../components/directOrders/IconTile';
import AttachmentsPanel from '../../components/directOrders/admin/AttachmentsPanel';
import ReferencePanel from '../../components/directOrders/admin/ReferencePanel';
import ExportPanel from '../../components/directOrders/admin/ExportPanel';
import ListsPanel from '../../components/directOrders/admin/ListsPanel';
import DeletePanel from '../../components/directOrders/admin/DeletePanel';
import ApproversPanel, { isSystemAdmin } from '../../components/directOrders/admin/ApproversPanel';
import DirectOrderViewModal from '../../components/DirectOrderViewModal';
import DirectOrderEditModal from '../../components/DirectOrderEditModal';
import { useDirectOrder } from '../../context/DirectOrderContext';
import { directOrderService } from '../../services/directOrderService';
import {
  canUserApprove,
  formatEuro,
  getInitiative,
  getOrderGroup,
  getOrderStatus,
  getReference,
} from '../../utils/directOrderStatus';

const { TextArea } = Input;

const ORDER_VIEWS = ['pending', 'approved', 'rejected', 'all'];

const VIEW_TITLES = {
  pending: 'Pending orders',
  approved: 'Approved orders',
  rejected: 'Rejected orders',
  all: 'All orders',
  attachments: 'Missing attachments',
  reference: 'Reference number',
  export: 'Export to CSV',
  lists: 'Initiatives and justifications',
  delete: 'Delete a direct order',
  approvers: 'Approvers',
};

const VIEW_SUBTITLES = {
  attachments: 'Find orders whose files are gone and upload replacements.',
  reference: 'Control the number the next direct order is given.',
  export: 'Download direct orders for a date range as a spreadsheet.',
  lists: 'The options people pick from on the New direct order form.',
  delete: 'Remove an order by its ID.',
  approvers: 'Who approves direct orders at each level. Only Admins can change this.',
};

// Older notification links used these tab names
const LEGACY_VIEWS = { reupload: 'attachments', settings: 'lists' };

const DirectOrderDashboard = () => {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    loadAllDirectOrders,
    allDirectOrders,
    loading,
    updateDirectOrderStatus,
    isApprover,
    isAdmin,
    approverLevel,
    user,
  } = useDirectOrder();

  const canDelete = isAdmin || isApprover;
  const canManageApprovers = isSystemAdmin(user);
  const requestedView = searchParams.get('view');
  const allowed = { delete: canDelete, approvers: canManageApprovers };
  const view = VIEW_TITLES[requestedView] && allowed[requestedView] !== false ? requestedView : null;

  const [activeYear, setActiveYear] = useState(new Date().getFullYear().toString());
  const [activeMonth, setActiveMonth] = useState('all');
  const [commentModalVisible, setCommentModalVisible] = useState(false);
  const [actionType, setActionType] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [comment, setComment] = useState('');
  const [viewOrder, setViewOrder] = useState(null);
  const [editOrder, setEditOrder] = useState(null);
  const [reference, setReference] = useState(null);

  // Only these users may edit orders that are already approved
  const canEditApproved =
    user?.email === 'claudette.debono-farrugia@festivals.mt' || user?.email === 'zach.zammit@festivals.mt';

  const openView = useCallback((next) => setSearchParams(next ? { view: next } : {}), [setSearchParams]);

  useEffect(() => {
    // Only load orders if the user is an approver or admin
    if (user && (isApprover || isAdmin)) {
      loadAllDirectOrders();
    }
  }, [loadAllDirectOrders, isApprover, approverLevel, isAdmin, user]);

  // Notification links open a specific queue through router state
  useEffect(() => {
    const requested = location.state?.activeTab;
    if (requested) {
      const mapped = LEGACY_VIEWS[requested] || requested;
      if (VIEW_TITLES[mapped]) {
        setSearchParams({ view: mapped }, { replace: true });
      }
      if (user && (isApprover || isAdmin)) {
        loadAllDirectOrders();
      }
    }
  }, [location.state?.activeTab, user, isApprover, isAdmin, loadAllDirectOrders, setSearchParams]);

  useEffect(() => {
    directOrderService
      .getCurrentReference()
      .then((result) => {
        if (result.success) setReference(result.currentReference);
      })
      .catch(() => {
        // the card just shows no value
      });
  }, []);

  // Warn if loading hangs
  useEffect(() => {
    if (loading) {
      const timeout = setTimeout(() => {
        message.warning('Loading is taking longer than expected. Please refresh the page.');
      }, 30000);

      return () => clearTimeout(timeout);
    }
  }, [loading]);

  const orders = useMemo(() => (Array.isArray(allDirectOrders) ? allDirectOrders : []), [allDirectOrders]);

  const counts = useMemo(() => {
    const result = { pending: 0, approved: 0, rejected: 0, all: orders.length };
    orders.forEach((order) => {
      result[getOrderGroup(order)] += 1;
    });
    return result;
  }, [orders]);

  const myQueue = useMemo(
    () => (isApprover ? orders.filter((order) => canUserApprove(order, user?.email)) : []),
    [orders, isApprover, user?.email]
  );

  const years = useMemo(() => {
    const set = new Set();
    orders.forEach((order) => {
      if (order.doDate) set.add(new Date(order.doDate).getFullYear());
    });
    return Array.from(set).sort((a, b) => b - a);
  }, [orders]);

  const months = useMemo(() => {
    const set = new Set();
    orders.forEach((order) => {
      if (order.doDate) {
        const date = new Date(order.doDate);
        if (date.getFullYear() === parseInt(activeYear, 10)) set.add(date.getMonth() + 1);
      }
    });
    return Array.from(set).sort((a, b) => a - b);
  }, [orders, activeYear]);

  const viewOrders = useMemo(
    () =>
      orders.filter((order) => {
        if (view === 'all') {
          if (!order.doDate) return true;
          const date = new Date(order.doDate);
          return (
            date.getFullYear() === parseInt(activeYear, 10) &&
            (activeMonth === 'all' || date.getMonth() + 1 === parseInt(activeMonth, 10))
          );
        }
        return getOrderGroup(order) === view;
      }),
    [orders, view, activeYear, activeMonth]
  );

  // Approve straight away; rejecting needs a reason first
  const handleAction = (record, action) => {
    setSelectedOrder(record);
    setActionType(action);

    if (action === 'approve') {
      handleSubmitAction(record, action, '');
    } else {
      setCommentModalVisible(true);
    }
  };

  const handleSubmitAction = async (order = selectedOrder, action = actionType, actionComment = comment) => {
    const targetOrder = order || selectedOrder;
    const targetAction = action || actionType;
    const targetComment = actionComment !== undefined ? actionComment : comment;

    if (!targetOrder || !targetOrder.doId) {
      message.error('Cannot identify which order to process. Please try again.');
      return;
    }

    if (targetAction === 'reject' && (!targetComment || targetComment.trim() === '')) {
      message.error('Comments are required when rejecting an order');
      return;
    }

    if (!targetAction) {
      message.error('Missing action information');
      return;
    }

    try {
      const result = await updateDirectOrderStatus(targetOrder.doId, targetAction, targetComment);

      // The context has already shown the error when the update fails
      if (!result) return;

      const actionPastTense = targetAction === 'approve' ? 'approved' : 'rejected';
      message.success(`Direct Order #${targetOrder.doId} has been ${actionPastTense} successfully!`, 5);

      setCommentModalVisible(false);
      setComment('');
      setSelectedOrder(null);
      setActionType(null);

      loadAllDirectOrders();
    } catch (error) {
      message.error(`Failed to ${targetAction} Direct Order #${targetOrder.doId}: ${error.message}`, 8);
    }
  };

  const handleModalSubmit = () => {
    if (!selectedOrder || !selectedOrder.doId) {
      message.error('Order information lost. Please close the modal and try again.');
      return;
    }

    handleSubmitAction(selectedOrder, actionType, comment);
  };

  const closeRejectModal = () => {
    setCommentModalVisible(false);
    setComment('');
    setSelectedOrder(null);
    setActionType(null);
  };

  // Approve / Reject / Edit buttons for an order, as the current user is allowed
  const renderActions = (order) => {
    const status = getOrderStatus(order);
    return (
      <>
        {isApprover && canUserApprove(order, user?.email) && (
          <>
            <Button type="primary" onClick={() => handleAction(order, 'approve')}>
              Approve
            </Button>
            {status !== 'rejected' && (
              <Button danger type="text" onClick={() => handleAction(order, 'reject')}>
                Reject
              </Button>
            )}
          </>
        )}
        {status === 'approved' && canEditApproved && (
          <Button type="text" onClick={() => setEditOrder(order)}>
            Edit
          </Button>
        )}
      </>
    );
  };

  const rejectModal = (
    <Modal
      title="Reject direct order"
      open={commentModalVisible}
      onOk={handleModalSubmit}
      onCancel={closeRejectModal}
      okText="Reject order"
      cancelText="Cancel"
      okButtonProps={{ danger: true }}
      rootClassName="do-root do-scope"
      width={520}
    >
      <p className="do-muted" style={{ margin: '0 0 16px' }}>
        <span className="do-ref" style={{ color: 'var(--do-ink)' }}>{selectedOrder ? getReference(selectedOrder) : ''}</span>
        {selectedOrder?.doSupplier ? ` · ${selectedOrder.doSupplier}` : ''}
      </p>
      <label htmlFor="do-reject-reason" style={{ display: 'block', fontWeight: 500, marginBottom: 6 }}>
        Reason for rejecting
      </label>
      <TextArea
        id="do-reject-reason"
        rows={4}
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        placeholder="Tell the requester what needs to change. This is required."
      />
    </Modal>
  );

  const detailModals = (
    <>
      {viewOrder && <DirectOrderViewModal visible directOrder={viewOrder} onClose={() => setViewOrder(null)} />}
      {editOrder && (
        <DirectOrderEditModal
          visible
          directOrder={editOrder}
          onClose={() => setEditOrder(null)}
          onSave={() => {
            setEditOrder(null);
            loadAllDirectOrders();
          }}
        />
      )}
    </>
  );

  /* ---------- A single view (queue, attachments, settings) ---------- */

  if (view) {
    const emptyByView = {
      pending: ['Nothing is waiting for review', 'New direct orders will appear here as they are raised.'],
      approved: ['No approved orders', 'Orders appear here once all three approvals are in.'],
      rejected: ['No rejected orders', 'Rejected orders appear here until they are resubmitted.'],
      all: ['No orders in this period', 'Choose a different year or month.'],
    };

    return (
      <DOLayout title={VIEW_TITLES[view]} subtitle={VIEW_SUBTITLES[view]}>
        {ORDER_VIEWS.includes(view) && (
          <OrdersExplorer
            orders={viewOrders}
            loading={loading}
            showSubmitter
            hideGroupFilter
            onOpen={setViewOrder}
            renderActions={renderActions}
            resetKey={view}
            emptyTitle={emptyByView[view][0]}
            emptyHint={emptyByView[view][1]}
            toolbarExtra={
              view === 'all' && (
                <>
                    <Select
                      value={activeYear}
                      onChange={(value) => {
                        setActiveYear(value);
                        setActiveMonth('all');
                      }}
                      options={years.map((year) => ({ value: year.toString(), label: year.toString() }))}
                      style={{ width: 96 }}
                      aria-label="Year"
                    />
                    <Select
                      value={activeMonth}
                      onChange={setActiveMonth}
                      options={[
                        { value: 'all', label: 'All months' },
                        ...months.map((month) => ({
                          value: month.toString(),
                          label: new Date(2000, month - 1).toLocaleString('en-GB', { month: 'long' }),
                        })),
                      ]}
                      style={{ width: 140 }}
                      aria-label="Month"
                    />
                </>
              )
            }
          />
        )}

        {view === 'attachments' && <AttachmentsPanel />}
        {view === 'reference' && <ReferencePanel reference={reference} onUpdated={setReference} />}
        {view === 'export' && <ExportPanel />}
        {view === 'lists' && <ListsPanel />}
        {view === 'delete' && canDelete && <DeletePanel />}
        {view === 'approvers' && canManageApprovers && <ApproversPanel />}

        {rejectModal}
        {detailModals}
      </DOLayout>
    );
  }

  /* ---------- Landing ---------- */

  const queuePreview = myQueue.slice(0, 6);
  const firstLoad = loading && orders.length === 0;
  const inApproval = orders.filter((order) => getOrderGroup(order) === 'pending');

  const summary = firstLoad
    ? 'Loading orders…'
    : `${counts.all} orders in total: ${counts.pending} in approval, ${counts.approved} approved and ${counts.rejected} rejected.`;

  return (
    <DOLayout title="Admin overview" subtitle={summary}>
      {isApprover && (
        <section className="do-block" aria-labelledby="do-queue-title">
          <header className="do-block__head">
            <h2 className="do-block__title" id="do-queue-title">Waiting for your approval</h2>
            {myQueue.length > queuePreview.length ? (
              <button type="button" className="do-link" onClick={() => openView('pending')}>
                See all {myQueue.length}
              </button>
            ) : (
              <span className="do-block__meta do-num">{myQueue.length}</span>
            )}
          </header>
          {queuePreview.length === 0 ? (
            <div className="do-empty do-empty--left">
              <strong>{firstLoad ? 'Checking the queue' : "You're all caught up"}</strong>
              {!firstLoad && 'Orders that need your approval will appear here.'}
            </div>
          ) : (
            <ul className="do-todo">
              {queuePreview.map((order) => (
                <li className="do-todo__item" key={order.doId}>
                  <IconTile icon={<AuditOutlined />} tone="accent" />
                  <div className="do-todo__text">
                    <button type="button" className="do-todo__title" onClick={() => setViewOrder(order)}>
                      {order.doSupplier || 'Unknown supplier'}
                    </button>
                    <span className="do-todo__meta">
                      {getReference(order)} · {formatEuro(order.doTotalCost)} · {order.doOfficerNameFull || 'Unknown officer'} ·{' '}
                      {getInitiative(order)}
                    </span>
                  </div>
                  <ApprovalTrack order={order} />
                  <div className="do-todo__action do-actions">{renderActions(order)}</div>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      <section className="do-block" aria-labelledby="do-approval-title">
        <header className="do-block__head">
          <h2 className="do-block__title" id="do-approval-title">In approval by festival</h2>
          <button type="button" className="do-link" onClick={() => openView('all')}>
            Open all orders
          </button>
        </header>
        <OrdersExplorer
          compact
          orders={inApproval}
          loading={firstLoad}
          showSubmitter
          onOpen={setViewOrder}
          renderActions={renderActions}
          emptyTitle="Nothing is in approval"
          emptyHint="New direct orders appear here while they go through the three approvals."
        />
      </section>

      {rejectModal}
      {detailModals}
    </DOLayout>
  );
};

export default DirectOrderDashboard;
